import { useReactOidc } from '@axa-fr/react-oidc-context';
import { useCallback, useContext, useEffect, useState } from 'react';
import useIdle from '../../hooks/useIdle';
import { useLogAction } from '../../hooks/useLogAction';
import { ConfigurationContext } from '../../providers/ConfigurationProvider';
import {
  INACTIVE_DECOUNCE,
  INACTIVE_TIMEOUT,
  LOCAL_STORAGE_INACTIVE_POPUP_ACTION,
} from './Common.constants';
import { AuditLogAction, InactivePopupAction } from './Common.types';
import {
  removeInActivePopupActionStorage,
  setInActivePopupAction,
} from './Common.utils';
import InactiveCountdownModal from './InactiveCountdownModal';

export default function InactivityHandler() {
  const { logAction } = useLogAction();
  const [isInactiveModalVisible, setIsInactiveModalVisible] = useState(false);
  const { logout } = useReactOidc();
  const handleLogout = (): void => {
    setIsCheckingInactiveIntervalEnabled(false);
    setInActivePopupAction(InactivePopupAction.LOGOUT);
    closeInactiveModal();
    logAction({ action: AuditLogAction.Logout, message: 'Due to inactivity' });
    logout();
  };
  const config = useContext(ConfigurationContext);
  const inactiveTimeout =
    config?.INACTIVITY_CONFIG.inactive_timeout || INACTIVE_TIMEOUT;

  const [
    isCheckingInactiveIntervalEnabled,
    setIsCheckingInactiveIntervalEnabled,
  ] = useState(false);

  useEffect(() => {
    if (isCheckingInactiveIntervalEnabled) {
      window.addEventListener('storage', checkInactiveActionInLocalStorage);
    }
    return () => {
      window.removeEventListener('storage', checkInactiveActionInLocalStorage);
    };
  }, [isCheckingInactiveIntervalEnabled]);

  const checkInactiveActionInLocalStorage = (e: StorageEvent): void => {
    if (e.key === LOCAL_STORAGE_INACTIVE_POPUP_ACTION) {
      const inActiveAction: InactivePopupAction | null | string = e.newValue;
      if (inActiveAction) {
        if (inActiveAction === InactivePopupAction.KEEPLOGIN) {
          handleKeepLogin();
        } else {
          handleLogout();
        }
        setIsCheckingInactiveIntervalEnabled(false);
      }
    }
  };

  const handleKeepLogin = (): void => {
    setIsCheckingInactiveIntervalEnabled(false);
    setInActivePopupAction(InactivePopupAction.KEEPLOGIN);
    closeInactiveModal();
  };

  const openInactiveModal = (): void => {
    setIsInactiveModalVisible(true);
  };

  const closeInactiveModal = (): void => {
    setIsInactiveModalVisible(false);
  };

  const handleOnIdle = (): void => {
    openInactiveModal();
    removeInActivePopupActionStorage();
    setIsCheckingInactiveIntervalEnabled(true);
  };

  const handleOnAction = (): void => {
    handleKeepLogin();
  };

  useIdle({
    timeout: inactiveTimeout,
    onIdle: handleOnIdle,
    onActive: handleOnAction,
    debounce: INACTIVE_DECOUNCE,
  });

  const memoizedKeepLogin = useCallback(handleKeepLogin, []);
  const memoizedLogout = useCallback(handleLogout, []);

  return (
    <InactiveCountdownModal
      isInactiveModalVisible={isInactiveModalVisible}
      handleKeepLogin={memoizedKeepLogin}
      handleLogout={memoizedLogout}
    />
  );
}
